import { useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import {
  AlertCircle,
  ArrowRight,
  Check,
  CheckCircle2,
  Clipboard,
  Gauge,
  Info,
  Loader2,
  LockKeyhole,
  Minus,
  Paperclip,
  RotateCcw,
  Sparkles,
  Target,
  TriangleAlert,
  Upload,
  X,
} from 'lucide-react';
import { analyse } from './api';
import type { Assessment, Gap, ScoreDimension } from './lib/assessment';
import { scoreDimensionLabels, SCORE_WEIGHTS, weightedScore } from './lib/assessment';
import { exampleJobAd, exampleResume } from './lib/mockAnalysis';
import { MAX_TEXT_LENGTH, validateInputs } from './lib/validation';

type ViewState = 'input' | 'results';

function LogoMark() {
  return (
    <span className="logo-mark" aria-hidden="true">
      <span className="logo-mark__ring logo-mark__ring--outer" />
      <span className="logo-mark__ring logo-mark__ring--inner" />
      <span className="logo-mark__dot" />
    </span>
  );
}

function Brand({ compact = false }: { compact?: boolean }) {
  return (
    <div className="brand" aria-label="RoleMatch home">
      <LogoMark />
      <span className={compact ? 'brand__name brand__name--compact' : 'brand__name'}>RoleMatch</span>
    </div>
  );
}

function CharacterCount({ value }: { value: string }) {
  return <span className="character-count">{value.length.toLocaleString()} / {MAX_TEXT_LENGTH.toLocaleString()}</span>;
}

function TextField({
  id,
  label,
  hint,
  value,
  onChange,
  placeholder,
  error,
  children,
}: {
  id: string;
  label: string;
  hint: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  error?: string;
  children?: ReactNode;
}) {
  return (
    <div className="field-block">
      <div className="field-label-row">
        <label htmlFor={id} className="field-label">{label}</label>
        <span className="field-hint">{hint}</span>
      </div>
      <div className={`textarea-wrap ${error ? 'textarea-wrap--error' : ''}`}>
        <textarea
          id={id}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          maxLength={MAX_TEXT_LENGTH}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? `${id}-error` : undefined}
        />
        <CharacterCount value={value} />
        {children}
      </div>
      {error && <p id={`${id}-error`} className="field-error"><AlertCircle size={15} />{error}</p>}
    </div>
  );
}

function InputPage({
  resume,
  jobAd,
  setResume,
  setJobAd,
  onSubmit,
  onExample,
  loading,
  error,
  pdfStatus,
  onPdf,
}: {
  resume: string;
  jobAd: string;
  setResume: (value: string) => void;
  setJobAd: (value: string) => void;
  onSubmit: () => void;
  onExample: () => void;
  loading: boolean;
  error?: { resumeError?: string; jobAdError?: string; formError?: string };
  pdfStatus?: { loading: boolean; fileName?: string; message?: string; error?: string };
  onPdf: (file: File) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <main className="input-page">
      <div className="background-orbit background-orbit--one" />
      <div className="background-orbit background-orbit--two" />
      <header className="site-header page-shell">
        <Brand />
        <div className="header-note"><LockKeyhole size={14} /> No account. No saved resumes.</div>
      </header>

      <section className="hero page-shell">
        <div className="hero__copy">
          <div className="eyebrow"><span className="eyebrow__line" /> A clearer call on your next move</div>
          <h1>Is this role worth <em>your</em> time?</h1>
          <p className="hero__lede">RoleMatch reads your experience against the real demands of a job ad, then gives you the candid view a keyword checker can’t.</p>
          <div className="hero__proof">
            <div className="proof-item"><span className="proof-icon"><Target size={16} /></span><span>Evidence-led, not keyword-led</span></div>
            <div className="proof-item"><span className="proof-icon proof-icon--mint"><Sparkles size={16} /></span><span>Useful advice, even when it’s a no</span></div>
          </div>
        </div>

        <div className="form-card">
          <div className="form-card__topline">
            <div>
              <p className="form-card__kicker">Start with the essentials</p>
              <h2>Tell us about the role</h2>
            </div>
            <span className="step-pill">01 <span>/ 01</span></span>
          </div>

          <TextField
            id="resume"
            label="Your resume"
            hint="Paste text or upload a PDF"
            value={resume}
            onChange={setResume}
            placeholder="Paste the text of your resume here…"
            error={error?.resumeError}
          >
            <div className="field-utility-row">
              <button type="button" className="upload-button" onClick={() => fileInputRef.current?.click()} disabled={pdfStatus?.loading}>
                {pdfStatus?.loading ? <Loader2 size={15} className="spin" /> : <Upload size={15} />}
                {pdfStatus?.loading ? 'Reading PDF…' : 'Upload PDF'}
              </button>
              {pdfStatus?.fileName && <span className="file-name"><Check size={13} /> {pdfStatus.fileName}</span>}
              <input ref={fileInputRef} className="visually-hidden" type="file" accept="application/pdf,.pdf" onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) onPdf(file);
                event.target.value = '';
              }} />
            </div>
          </TextField>
          {pdfStatus?.error && <p className="field-error pdf-error"><AlertCircle size={15} />{pdfStatus.error}</p>}
          {pdfStatus?.message && !pdfStatus.error && <p className="field-success"><CheckCircle2 size={15} />{pdfStatus.message}</p>}

          <TextField
            id="job-ad"
            label="Job advertisement"
            hint="The more complete, the better"
            value={jobAd}
            onChange={setJobAd}
            placeholder="Paste the full job advertisement here…"
            error={error?.jobAdError}
          />

          {error?.formError && <div className="form-error-banner"><AlertCircle size={17} /><span>{error.formError}</span></div>}
          <button type="button" className="primary-button" onClick={onSubmit} disabled={loading}>
            {loading ? <><Loader2 size={18} className="spin" /> Reading both sides…</> : <>Check my match <ArrowRight size={18} /></>}
          </button>
          <div className="form-card__footer">
            <span><LockKeyhole size={13} /> Sent to Cloudflare Workers AI for this assessment; not stored by RoleMatch</span>
            <button type="button" className="example-button" onClick={onExample}><Paperclip size={13} /> Try an example</button>
          </div>
        </div>
      </section>

      <footer className="site-footer page-shell">
        <span>RoleMatch is an estimate, not a hiring decision.</span>
        <span className="footer-divider" />
        <span>AI-assisted advice with a human point of view.</span>
      </footer>
    </main>
  );
}

function ScoreDial({ score }: { score: number }) {
  const ratingClass = score >= 80 ? 'score-dial--high' : score >= 60 ? 'score-dial--mid' : 'score-dial--low';
  return (
    <div className={`score-dial ${ratingClass}`} style={{ '--score': `${score * 3.6}deg` } as CSSProperties}>
      <div className="score-dial__inner">
        <span className="score-dial__number">{score}</span>
        <span className="score-dial__out-of">out of 100</span>
      </div>
    </div>
  );
}

function VerdictPill({ verdict }: { verdict: Assessment['verdict'] }) {
  const icon = verdict === 'Apply' ? <CheckCircle2 size={16} /> : verdict === 'Worth exploring' ? <Minus size={17} /> : <X size={16} />;
  return <span className={`verdict-pill verdict-pill--${verdict.toLowerCase().replace(' ', '-')}`}>{icon}{verdict}</span>;
}

function SectionHeading({ eyebrow, title, icon }: { eyebrow: string; title: string; icon: ReactNode }) {
  return (
    <div className="section-heading">
      <span className="section-heading__icon">{icon}</span>
      <div><p>{eyebrow}</p><h3>{title}</h3></div>
    </div>
  );
}

function GapItem({ gap }: { gap: Gap }) {
  return (
    <li className="gap-item">
      <span className={`gap-type gap-type--${gap.type}`} aria-hidden="true">{gap.type === 'missing' ? '!' : '?'}</span>
      <div><strong>{gap.label}</strong><p>{gap.detail}</p></div>
    </li>
  );
}

function ResultsPage({ assessment, onReset }: { assessment: Assessment; onReset: () => void }) {
  const [copied, setCopied] = useState(false);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const scoreDescription = assessment.score >= 80 ? 'This one deserves a serious look.' : assessment.score >= 60 ? 'There is enough here to investigate.' : 'The gaps are meaningful right now.';

  const resultText = useMemo(() => {
    const breakdown = Object.entries(assessment.scoreBreakdown)
      .map(([key, value]) => `${scoreDimensionLabels[key as ScoreDimension]}: ${value}/100`)
      .join('\n');
    return `My RoleMatch assessment — ${assessment.score}/100 (${assessment.rating})\n\nVerdict: ${assessment.verdict}\n${assessment.verdictSummary}\n\nWhere I match\n${assessment.strengths.map((item) => `• ${item}`).join('\n')}\n\nPotential gaps\n${assessment.gaps.map((item) => `• ${item.label}: ${item.detail}`).join('\n')}\n\nThings to watch out for\n${assessment.watchOuts.map((item) => `• ${item}`).join('\n')}\n\nHow to improve my application\n${assessment.improvements.map((item) => `• ${item}`).join('\n')}\n\nRecommended next step\n${assessment.nextStep}\n\nScore dimensions\n${breakdown}`;
  }, [assessment]);

  async function copyResults() {
    try {
      await navigator.clipboard.writeText(resultText);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2200);
    } catch {
      setCopied(false);
    }
  }

  return (
    <main className="results-page">
      <header className="results-header page-shell">
        <Brand compact />
        <div className="results-actions">
          <button type="button" className="text-button" onClick={onReset}><RotateCcw size={15} /> Check another role</button>
          <button type="button" className="copy-button" onClick={copyResults}>{copied ? <Check size={15} /> : <Clipboard size={15} />}{copied ? 'Copied' : 'Copy results'}</button>
        </div>
      </header>

      <div className="results-shell page-shell">
        <div className="results-intro">
          <div>
            <div className="eyebrow"><span className="eyebrow__line" /> Your role match</div>
            <h1>A useful read on your<br className="desktop-only" /> next move.</h1>
          </div>
          <p>Weighed against the role’s essentials, the evidence in your resume, and the practical questions worth asking next.</p>
        </div>

        <section className="score-summary">
          <div className="score-summary__main">
            <ScoreDial score={assessment.score} />
            <div className="score-summary__copy">
              <p className="score-summary__eyebrow">Overall match</p>
              <h2>{assessment.rating}</h2>
              <p>{scoreDescription}</p>
              <button type="button" className="score-detail-button" onClick={() => setShowBreakdown((value) => !value)}>
                <Gauge size={15} /> {showBreakdown ? 'Hide score dimensions' : 'See score dimensions'} <ArrowRight size={14} className={showBreakdown ? 'rotate-90' : ''} />
              </button>
            </div>
          </div>
          <div className="score-summary__note"><Info size={15} /><span>This is an estimated assessment of fit, not a probability of getting hired.</span></div>
        </section>

        {showBreakdown && <ScoreBreakdown breakdown={assessment.scoreBreakdown} />}

        <section className="verdict-card">
          <div className="verdict-card__top"><p className="card-eyebrow">The verdict</p><VerdictPill verdict={assessment.verdict} /></div>
          <p className="verdict-card__text">{assessment.verdictSummary}</p>
        </section>

        <div className="results-grid">
          <section className="insight-card insight-card--strengths">
            <SectionHeading eyebrow="The evidence" title="Where you match" icon={<Target size={18} />} />
            <ul className="insight-list">{assessment.strengths.map((strength) => <li key={strength}><span className="list-check"><Check size={14} /></span><span>{strength}</span></li>)}</ul>
          </section>

          <section className="insight-card insight-card--gaps">
            <SectionHeading eyebrow="Read this carefully" title="Potential gaps" icon={<TriangleAlert size={18} />} />
            <ul className="gap-list">{assessment.gaps.map((gap) => <GapItem key={`${gap.label}-${gap.type}`} gap={gap} />)}</ul>
            <p className="card-note"><Info size={14} /> “Not evidenced” is different from “missing”. Your resume may simply need a clearer example.</p>
          </section>
        </div>

        <section className="watch-card">
          <div className="watch-card__head"><SectionHeading eyebrow="Before you invest time" title="Things to watch out for" icon={<AlertCircle size={18} />} /><span className="watch-card__label">Questions worth asking</span></div>
          <div className="watch-list">{assessment.watchOuts.map((item, index) => <div className="watch-item" key={item}><span>0{index + 1}</span><p>{item}</p></div>)}</div>
        </section>

        <section className="improve-card">
          <div className="improve-card__head"><div><p className="card-eyebrow">Make the application stronger</p><h3>How to improve your application</h3></div><span className="improve-card__spark"><Sparkles size={20} /></span></div>
          <div className="improvement-list">{assessment.improvements.map((item, index) => <div className="improvement-item" key={item}><span className="improvement-number">{index + 1}</span><p>{item}</p></div>)}</div>
        </section>

        <section className="next-step-card">
          <div><p className="card-eyebrow">Your recommended next step</p><h3>{assessment.nextStep}</h3><p className="uncertainty"><Info size={14} /> {assessment.uncertainty}</p></div>
          <button type="button" className="outline-button" onClick={onReset}>Assess another role <ArrowRight size={16} /></button>
        </section>

        <footer className="results-footer"><span><LockKeyhole size={14} /> Processed by Cloudflare Workers AI; not stored by RoleMatch.</span><span>RoleMatch uses AI to support your judgement, not replace it.</span></footer>
      </div>
    </main>
  );
}

function ScoreBreakdown({ breakdown }: { breakdown: Assessment['scoreBreakdown'] }) {
  return (
    <section className="breakdown-card" aria-label="Score dimensions">
      <div className="breakdown-card__intro"><p className="card-eyebrow">How the score was built</p><p>Each dimension is weighted to reflect what usually matters in a real screening decision.</p></div>
      <div className="breakdown-bars">{Object.entries(breakdown).map(([dimension, value]) => <div className="bar-row" key={dimension}><div className="bar-row__label"><span>{scoreDimensionLabels[dimension as ScoreDimension]}</span><small>{SCORE_WEIGHTS[dimension as ScoreDimension]}% weight</small><strong>{value}</strong></div><div className="bar-track"><span style={{ width: `${value}%` }} /></div></div>)}</div>
      <p className="breakdown-total">Weighted total: <strong>{weightedScore(breakdown)}/100</strong></p>
    </section>
  );
}

export default function App() {
  const [view, setView] = useState<ViewState>('input');
  const [resume, setResume] = useState('');
  const [jobAd, setJobAd] = useState('');
  const [loading, setLoading] = useState(false);
  const [assessment, setAssessment] = useState<Assessment | null>(null);
  const [error, setError] = useState<{ resumeError?: string; jobAdError?: string; formError?: string }>({});
  const [pdfStatus, setPdfStatus] = useState<{ loading: boolean; fileName?: string; message?: string; error?: string }>({ loading: false });

  function loadExample() {
    setResume(exampleResume);
    setJobAd(exampleJobAd);
    setError({});
    setPdfStatus({ loading: false });
  }

  async function handlePdf(file: File) {
    setPdfStatus({ loading: true });
    try {
      const { extractPdfText } = await import('./lib/pdf');
      const extracted = await extractPdfText(file);
      setResume(extracted.text.slice(0, MAX_TEXT_LENGTH));
      setPdfStatus({ loading: false, fileName: file.name, message: `Extracted text from ${extracted.pages} page${extracted.pages === 1 ? '' : 's'} — you can edit it above.` });
      setError((current) => ({ ...current, resumeError: undefined }));
    } catch (pdfError) {
      setPdfStatus({ loading: false, error: pdfError instanceof Error ? pdfError.message : 'We could not read that PDF.' });
    }
  }

  async function handleSubmit() {
    const validation = validateInputs(resume, jobAd);
    setError(validation);
    if (validation.resumeError || validation.jobAdError) return;

    setLoading(true);
    try {
      const result = await analyse({ resume: resume.trim(), jobAd: jobAd.trim() });
      setAssessment(result);
      setView('results');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (analysisError) {
      setError({ formError: analysisError instanceof Error ? analysisError.message : 'We could not complete the assessment. Please try again.' });
    } finally {
      setLoading(false);
    }
  }

  function reset() {
    setView('input');
    setAssessment(null);
    setJobAd('');
    setError({});
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  return view === 'results' && assessment ? <ResultsPage assessment={assessment} onReset={reset} /> : <InputPage resume={resume} jobAd={jobAd} setResume={setResume} setJobAd={setJobAd} onSubmit={handleSubmit} onExample={loadExample} loading={loading} error={error} pdfStatus={pdfStatus} onPdf={handlePdf} />;
}
